import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField, Typography,
} from '@mui/material';
import { DATE_PRESETS, DEFAULT_DATE_PRESET, type DatePresetKey } from '../../utils/date-presets';
import { SORTABLE_FIELDS } from '../../utils/user-settings';
import {
  PRESET_FIELDS, capturePreset, matchingDatePreset,
  type PresetField, type PresetMatchState, type TrackingPreset,
} from '../../utils/tracking-presets';

const sortLabel = (field: string) => (field === 'name'
  ? 'Name' : SORTABLE_FIELDS.find((f) => f.value === field)?.label ?? field);

interface Props {
  open: boolean;
  onClose: () => void;
  state: PresetMatchState;
  vLabel: (field: string) => string;
  onSave: (preset: TrackingPreset) => void;
}

export default function SavePresetDialog({ open, onClose, state, vLabel, onSave }: Props) {
  const [name, setName] = useState('');
  const [fields, setFields] = useState<Set<PresetField>>(new Set());
  const [datePreset, setDatePreset] = useState<DatePresetKey>(DEFAULT_DATE_PRESET);

  // seed on open only (re-seeding mid-edit wipes edits)
  useEffect(() => {
    if (!open) return;
    setName('');
    setDatePreset(matchingDatePreset(state.dateRange) ?? DEFAULT_DATE_PRESET);
    const f = new Set<PresetField>(['group']);
    if (state.drilldownChain.length) f.add('drilldown');
    if (state.filters.length) f.add('filters');
    if (state.tagIds.length) f.add('tags');
    setFields(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key: PresetField) => setFields((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const preview = useMemo((): Record<PresetField, string> => ({
    date: DATE_PRESETS.find((p) => p.key === datePreset)?.title ?? datePreset,
    group: vLabel(state.groupBy),
    drilldown: state.drilldownChain.length ? state.drilldownChain.map(vLabel).join(' › ') : 'None',
    filters: state.filters.length ? `${state.filters.length} selected` : 'None',
    tags: state.tagIds.length ? `${state.tagIds.length} selected` : 'None',
    sort: `${sortLabel(state.sortBy)} ${state.sortOrder === 'asc' ? '↑' : '↓'}`,
  }), [datePreset, state, vLabel]);

  const canSave = name.trim().length > 0 && fields.size > 0;

  const handleSave = () => {
    const id = crypto.randomUUID?.() ?? `p-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    onSave(capturePreset(id, name.trim(), fields, state, datePreset));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Save current view</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus fullWidth size="small" label="Preset name" value={name}
          onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          Pick what this preset sets. Unticked fields are left untouched when you apply it.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          {PRESET_FIELDS.map(({ key, label }) => (
            <Box key={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={fields.has(key)} onChange={() => toggle(key)} />}
                  label={label}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, textAlign: 'right' }}>
                  {preview[key]}
                </Typography>
              </Box>
              {key === 'date' && fields.has('date') && (
                <TextField
                  select size="small" value={datePreset} sx={{ ml: 4, mb: 1, minWidth: 180 }}
                  onChange={(e) => setDatePreset(e.target.value as DatePresetKey)}
                >
                  {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
                </TextField>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
